<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
    defineProps<{
        full?: boolean;
        height?: string | null;
        width?: string | null;
    }>(),
    {
        height: null,
        width: null,
    }
);

const viewportHeight = 53.55;
const viewportWidthFull = 231.75;
const viewportWidthNoText = 93;

const viewportWidth = computed(() =>
    props.full ? viewportWidthFull : viewportWidthNoText
);

const ratio = computed(() => viewportWidth.value / viewportHeight);

const style = computed(() => {
    if (props.height === null && props.width === null) {
        return { width: '100%', height: 'auto' };
    }

    if (props.height === null) {
        return {
            width: props.width as string,
            height: `calc(${props.height} / ${ratio.value})`,
        };
    }

    if (props.width === null) {
        return {
            width: `calc(${props.height} * ${ratio.value})`,
            height: props.height as string,
        };
    }

    return {
        width: props.width,
        height: props.height,
    };
});
</script>

<template>
    <svg
        class="precon-logo"
        data-name="Precon Logo"
        xmlns="http://www.w3.org/2000/svg"
        :viewBox="`0 0 ${viewportWidth} ${viewportHeight}`"
        :style="style"
    >
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-p"
            d="m104.72,47.77h-6.15v-27.38h13.21c1.49,0,2.87.35,4.13,1.06,1.26.71,2.27,1.68,3.01,2.93.74,1.25,1.12,2.65,1.12,4.19v.24c0,1.52-.37,2.92-1.1,4.21-.73,1.29-1.73,2.31-2.99,3.05-1.27.75-2.65,1.12-4.17,1.12h-7.07v10.58Zm0-15.41h5.63c1.01,0,1.85-.34,2.51-1.02.66-.68,1-1.47,1-2.38v-.24c0-.93-.33-1.74-.98-2.41-.65-.68-1.5-1.02-2.54-1.02h-5.63v7.06Z"
        />
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-r"
            d="m134.9,34.35c0-.82-.23-1.54-.68-2.13-.45-.6-1.17-.9-2.16-.9-.93,0-1.64.37-2.14,1.1-.49.73-.74,1.64-.74,2.74v12.61h-5.75v-20.4h4.31l.72,3c.11-.29.38-.71.82-1.24.44-.53,1.08-1.03,1.92-1.48.84-.45,1.83-.68,2.98-.68,1.83,0,3.25.57,4.25,1.72,1,1.14,1.5,2.66,1.5,4.55,0,.4-.02.8-.06,1.2-.04.4-.07.68-.1.84h-4.87v-.92Z"
        />
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-e-acute"
            d="m160.69,44.63c-.85,1.21-1.98,2.1-3.37,2.67-1.4.58-2.92.86-4.57.86h-.64c-1.89,0-3.59-.4-5.11-1.2-1.52-.8-2.71-1.95-3.57-3.45-.86-1.5-1.3-3.29-1.3-5.37v-1.16c0-2.05.44-3.83,1.32-5.35.88-1.52,2.07-2.67,3.57-3.47,1.5-.8,3.2-1.2,5.09-1.2h.64c1.65,0,3.18.34,4.59,1.02,1.41.68,2.54,1.66,3.39,2.96.85,1.29,1.28,2.81,1.28,4.57v3.03h-13.97v.96c0,1.33.39,2.44,1.18,3.33.78.89,1.86,1.34,3.21,1.34,1.22,0,2.19-.34,2.89-1.02.7-.68,1.06-1.68,1.06-3.01h5.59c0,1.78-.42,3.28-1.28,4.49Zm-11.4-12.44c-.73.83-1.15,1.88-1.26,3.16h8.06c0-1.36-.31-2.43-.94-3.21-.62-.78-1.63-1.18-3.01-1.18-1.17,0-2.12.41-2.86,1.24Zm.9-7.38l3.67-5.79h5.15l.04.12-4.83,5.67h-4.03Z"
        />
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-c"
            d="m172.47,42.83c.69.89,1.62,1.34,2.79,1.34s2.19-.38,2.89-1.14c.7-.76,1.06-1.88,1.06-3.38h5.51c0,1.89-.41,3.47-1.22,4.75-.81,1.28-1.88,2.22-3.21,2.83-1.33.61-2.8.92-4.39.92h-.64c-2.98,0-5.35-.89-7.11-2.66-1.75-1.77-2.63-4.22-2.63-7.36v-1.16c0-2.08.42-3.87,1.26-5.37.84-1.5,1.99-2.65,3.45-3.45,1.46-.8,3.14-1.2,5.03-1.2h.64c1.57,0,3.03.34,4.37,1.02,1.34.68,2.42,1.66,3.23,2.96.81,1.29,1.22,2.81,1.22,4.57h-5.79c0-1.38-.3-2.49-.92-3.32-.61-.82-1.52-1.24-2.71-1.24s-2.14.47-2.84,1.42c-.69.94-1.04,2.14-1.04,3.58v3.55c0,1.33.35,2.44,1.04,3.33Z"
        />
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-o"
            d="m207.46,43.49c-.87,1.52-2.06,2.67-3.57,3.47-1.52.8-3.22,1.2-5.11,1.2h-.64c-1.89,0-3.59-.4-5.11-1.2-1.52-.8-2.71-1.96-3.57-3.47-.87-1.52-1.3-3.3-1.3-5.35v-1.16c0-2.05.43-3.83,1.3-5.35.86-1.52,2.06-2.67,3.57-3.47s3.22-1.2,5.11-1.2h.64c1.89,0,3.59.4,5.11,1.2s2.71,1.96,3.57,3.47c.86,1.52,1.3,3.3,1.3,5.35v1.16c0,2.05-.43,3.83-1.3,5.35Zm-5.83-11.26c-.81-.85-1.87-1.28-3.17-1.28s-2.36.43-3.17,1.28c-.81.85-1.22,2.09-1.22,3.71v3.23c0,1.62.41,2.86,1.22,3.71.81.85,1.87,1.28,3.17,1.28s2.36-.43,3.17-1.28c.81-.85,1.22-2.09,1.22-3.71v-3.23c0-1.62-.41-2.86-1.22-3.71Z"
        />
        <path
            class="precon-logo-secondary precon-logo-letter precon-logo-n"
            d="m218.09,30.36c1.68-2.26,3.85-3.39,6.5-3.39,2.08,0,3.79.56,5.13,1.68,1.34,1.12,2.02,2.74,2.02,4.87v14.25h-5.83v-12.97c0-1.04-.3-1.9-.9-2.57-.6-.68-1.37-1.02-2.3-1.02-1.04,0-1.93.39-2.67,1.18-.75.79-1.12,1.74-1.12,2.85v12.54h-5.83v-20.4h4.35l.64,3Z"
        />
        <path
            class="precon-logo-secondary precon-logo-spear precon-logo-spear-upper"
            d="m76.26.01h0s-19.15,12.58-33.45,22.6c-.73.51-4.44,3.14-8.86,6.26.26.27.72.58,1.48.49,0,0,7.46-5.17,8.15-5.65C57.87,13.7,76.26.01,76.26.01Z"
        />
        <path
            class="precon-logo-secondary precon-logo-spear precon-logo-spear-lower"
            d="m33.95,28.87c-7.56,5.35-16.77,11.88-16.77,11.88-.24.16-.29.49-.13.72.17.23.48.29.72.13,0,0,9.78-6.77,17.47-12.1-.63-.08-1.04-.32-1.28-.62Z"
        />
        <g class="precon-logo-stalk">
            <path
                class="precon-logo-primary"
                d="m14.99,43.48c.27-.57,1.32-1.39,1.75-1.18.44.21.46,1.56.2,2.11-.29.63-1.31,1.39-1.75,1.18-.44-.21-.5-1.48-.2-2.11Z"
            />
            <path
                class="precon-logo-primary"
                d="m12.19,45.43c.27-.58,1.32-1.4,1.75-1.19.44.2.46,1.56.2,2.1-.3.63-1.31,1.39-1.75,1.19-.44-.21-.5-1.48-.2-2.1Z"
            />
            <path
                class="precon-logo-primary"
                d="m9.4,47.37c.27-.57,1.31-1.4,1.75-1.19.44.2.46,1.55.2,2.11-.3.63-1.32,1.39-1.75,1.18-.44-.2-.49-1.47-.2-2.1Z"
            />
            <path
                class="precon-logo-primary"
                d="m6.6,49.31c.27-.57,1.32-1.39,1.75-1.19.44.21.46,1.56.2,2.11-.3.63-1.32,1.4-1.75,1.19-.44-.21-.5-1.48-.2-2.11Z"
            />
            <path
                class="precon-logo-primary"
                d="m3.8,51.25c.27-.57,1.32-1.39,1.75-1.19.44.21.46,1.56.2,2.11-.3.63-1.31,1.39-1.75,1.18-.44-.21-.5-1.48-.2-2.11Z"
            />
            <path
                class="precon-logo-primary"
                d="m.95,51.39c.52-.36,1.83-.55,2.11-.16.28.4-.38,1.58-.88,1.92-.57.4-1.84.55-2.11.16-.28-.4.31-1.53.88-1.93Z"
            />
            <path
                class="precon-logo-primary"
                d="m14.5,42.77c.63-.05,1.77-.74,1.73-1.23-.04-.48-1.3-.97-1.9-.93-.7.06-1.76.75-1.73,1.23.04.48,1.21.99,1.9.93Z"
            />
            <path
                class="precon-logo-primary"
                d="m11.7,44.71c.63-.05,1.77-.75,1.73-1.23-.04-.48-1.3-.97-1.9-.92-.69.05-1.76.74-1.73,1.22.04.48,1.21.98,1.9.92Z"
            />
            <path
                class="precon-logo-primary"
                d="m8.9,46.65c.63-.05,1.77-.74,1.72-1.22-.04-.49-1.3-.98-1.9-.93-.69.05-1.76.74-1.73,1.23.04.48,1.21.98,1.9.92Z"
            />
            <path
                class="precon-logo-primary"
                d="m6.1,48.6c.63-.05,1.77-.75,1.73-1.22-.04-.48-1.3-.98-1.9-.93-.69.06-1.76.74-1.72,1.22.04.48,1.21.98,1.9.92Z"
            />
            <path
                class="precon-logo-primary"
                d="m3.3,50.54c.63-.05,1.77-.74,1.73-1.23-.04-.48-1.3-.97-1.91-.92-.69.06-1.77.74-1.73,1.22.04.48,1.21.98,1.91.93Z"
            />
        </g>
        <path
            class="precon-logo-primary precon-logo-man"
            d="m92.21,16.52c-1.05.05-2.22.22-3.51,1.41-1.37,1.26-.86,1.15-2.14,2.07-2.78,2.01-7.81,3.38-10.06,5.07-4,3.01-8.6,2.21-13.54,5.17-1.05.63-2.1.99-3-.38,0,0,3.23-.4,2.08-1.94-.24-.37.04-.54-.21-1.04-.16-.33-.33-.58-.33-.58,0,0,.98-.79-.26-1.17-.27-.08-1.32-.41-1.53-.49-.21-.08-.16-.35.04-.4.45-.12.75-.2.75-.2,0,0-.2-.72-.98-1.2,0,0,.15-2.56-2.95-2.56-4.86,0-6.1,2.79-6.1,4.55,0,2.81,1.09,3.85,3.39,4.77,1.19.48,1.68,1.45,1.79,2,0,0,2.61.18,4,2.24,0,0-1.48-1.31-3.89-.8-1.16.19-2.05.59-3.02,1.79-.44.55-.69.75-1.16,1.03-1.48.87-2.37,1.08-3.83,2.35,0,0-2.33-.07-7.56-7.24-2.3-3.15-2.75-1.17-4.49-3.12-.84-.96-2.11-.54-3.27.16-.96.57-2.18,1.66-1.57,2.63,1.24,1.98,2.63,1.2,4.63.92,1.07-.15,1.63.21,1.84.44.78.83.95,1.64,2.51,4.26,1.69,2.85,2.12,3.45,4.13,5.21,1.8,1.58,1.35,1.97,3.36,1.59,2.74-.52,5.76-1.56,7.78-3.29.03-.03,4.13,1.71,6.3,7.98h12.23s-1.71-8.69-5.95-13.95c0,0,4.94-3.07,11.22-4.92,5.07-1.5,7.24-6.59,9.46-7.25,4.19-1.25,2.22-2.13,3.77-2.78,2.47-1.04,1.77-2.39.08-2.31Z"
        />
        <g class="admin" transform="translate(163)">
            <path
                id="0"
                d="M 2.42 13.72 L 0 13.72 L 5.2 0 L 8.08 0 L 13.26 13.72 L 10.66 13.72 L 9.52 10.74 L 3.56 10.74 L 2.42 13.72 Z M 6.5 2.7 L 4.2 8.92 L 8.86 8.92 L 6.58 2.7 L 6.5 2.7 Z"
                vector-effect="non-scaling-stroke"
            />
            <path
                id="1"
                d="M 20.2 13.72 L 15.02 13.72 L 15.02 0 L 20.2 0 A 7.006 7.006 0 0 1 21.676 0.151 A 6.087 6.087 0 0 1 22.39 0.35 Q 23.4 0.7 24.18 1.44 A 4.609 4.609 0 0 1 25.087 2.656 A 5.851 5.851 0 0 1 25.39 3.34 A 6.428 6.428 0 0 1 25.695 4.511 Q 25.789 5.07 25.812 5.707 A 11.79 11.79 0 0 1 25.82 6.14 L 25.82 7.18 Q 25.82 8.8 25.39 10.02 A 6.269 6.269 0 0 1 24.796 11.269 A 5.185 5.185 0 0 1 24.18 12.07 Q 23.4 12.9 22.39 13.31 A 5.662 5.662 0 0 1 20.529 13.712 A 6.595 6.595 0 0 1 20.2 13.72 Z M 17.5 1.92 L 17.5 11.8 L 20.02 11.8 A 3.408 3.408 0 0 0 21.128 11.623 A 3.125 3.125 0 0 0 21.65 11.39 A 2.587 2.587 0 0 0 22.507 10.599 A 3.484 3.484 0 0 0 22.8 10.09 A 3.855 3.855 0 0 0 23.064 9.308 Q 23.152 8.926 23.19 8.478 A 8.666 8.666 0 0 0 23.22 7.74 L 23.22 5.74 A 8.065 8.065 0 0 0 23.179 4.9 Q 23.089 4.041 22.8 3.47 A 2.947 2.947 0 0 0 22.356 2.814 A 2.352 2.352 0 0 0 21.65 2.28 A 3.517 3.517 0 0 0 20.379 1.935 A 4.258 4.258 0 0 0 20.02 1.92 L 17.5 1.92 Z"
                vector-effect="non-scaling-stroke"
            />
            <path
                id="2"
                d="M 30.82 13.72 L 28.6 13.72 L 28.6 0 L 32.6 0 L 36.46 10.1 L 36.52 10.1 L 40.38 0 L 44.4 0 L 44.4 13.72 L 41.94 13.72 L 41.94 2.86 L 41.84 2.86 L 37.72 13.72 L 35.02 13.72 L 30.92 2.86 L 30.82 2.86 L 30.82 13.72 Z"
                vector-effect="non-scaling-stroke"
            />
            <path
                id="3"
                d="M 52.94 13.72 L 47.26 13.72 L 47.26 11.8 L 48.86 11.8 L 48.86 1.92 L 47.26 1.92 L 47.26 0 L 52.94 0 L 52.94 1.92 L 51.34 1.92 L 51.34 11.8 L 52.94 11.8 L 52.94 13.72 Z"
                vector-effect="non-scaling-stroke"
            />
            <path
                id="4"
                d="M 58.04 13.72 L 55.8 13.72 L 55.8 0 L 57.88 0 L 63.22 8.02 L 63.98 9.3 L 64.06 9.28 L 64.06 0 L 66.3 0 L 66.3 13.72 L 64.16 13.72 L 58.66 5.44 L 58.12 4.48 L 58.04 4.5 L 58.04 13.72 Z"
                vector-effect="non-scaling-stroke"
            />
        </g>
        <g class="gino" transform="translate(183)">
            <g id="gino" font-size="9pt">
                <path
                    d="M 5.64 14.12 L 5.48 14.12 Q 4.3 14.12 3.3 13.72 Q 2.3 13.32 1.56 12.53 Q 0.82 11.74 0.41 10.56 A 7.003 7.003 0 0 1 0.109 9.318 Q 0.017 8.717 0.003 8.035 A 12.098 12.098 0 0 1 0 7.78 L 0 6.32 Q 0 4.74 0.43 3.55 A 5.698 5.698 0 0 1 1.031 2.338 A 4.782 4.782 0 0 1 1.66 1.57 Q 2.46 0.78 3.53 0.39 A 6.511 6.511 0 0 1 5.28 0.021 A 7.735 7.735 0 0 1 5.86 0 L 6.22 0 A 6.695 6.695 0 0 1 7.76 0.173 A 5.983 5.983 0 0 1 8.28 0.32 Q 9.24 0.64 9.98 1.27 A 4.308 4.308 0 0 1 11.072 2.704 A 5.021 5.021 0 0 1 11.14 2.85 A 4.821 4.821 0 0 1 11.491 4.08 A 6.383 6.383 0 0 1 11.56 5.04 L 8.86 5.04 Q 8.86 4.289 8.657 3.757 A 2.143 2.143 0 0 0 8.47 3.38 Q 8.08 2.76 7.43 2.49 A 3.499 3.499 0 0 0 6.527 2.255 A 4.473 4.473 0 0 0 5.96 2.22 A 3.554 3.554 0 0 0 5.168 2.306 A 3.105 3.105 0 0 0 4.81 2.41 Q 4.28 2.6 3.89 3.01 Q 3.506 3.414 3.296 4.071 A 3.909 3.909 0 0 0 3.29 4.09 Q 3.117 4.641 3.087 5.387 A 8.091 8.091 0 0 0 3.08 5.72 L 3.08 8.4 A 7.387 7.387 0 0 0 3.11 9.079 Q 3.142 9.428 3.21 9.729 A 3.793 3.793 0 0 0 3.29 10.03 A 3.314 3.314 0 0 0 3.508 10.567 Q 3.671 10.879 3.89 11.11 Q 4.28 11.52 4.81 11.71 Q 5.34 11.9 5.96 11.9 A 4.823 4.823 0 0 0 6.699 11.846 A 4.019 4.019 0 0 0 7.11 11.76 A 2.544 2.544 0 0 0 7.845 11.44 A 2.359 2.359 0 0 0 8.04 11.3 A 1.963 1.963 0 0 0 8.522 10.726 A 2.481 2.481 0 0 0 8.66 10.44 A 2.672 2.672 0 0 0 8.803 9.955 Q 8.851 9.713 8.869 9.431 A 5.449 5.449 0 0 0 8.88 9.08 L 8.88 8.5 L 5.58 8.5 L 5.58 6.52 L 11.56 6.52 L 11.56 11.24 Q 11.56 12.117 11.6 12.713 A 11.219 11.219 0 0 0 11.61 12.85 A 45.01 45.01 0 0 1 11.643 13.296 Q 11.655 13.47 11.665 13.627 A 22.116 22.116 0 0 1 11.68 13.92 L 10.08 13.92 L 9.62 12.3 A 3.869 3.869 0 0 1 8.42 13.376 A 4.895 4.895 0 0 1 7.9 13.64 Q 6.8 14.12 5.64 14.12 Z M 25.22 13.92 L 22.58 13.92 L 22.58 0.2 L 25.02 0.2 L 29.92 7.48 L 30.64 8.74 L 30.72 8.72 L 30.72 0.2 L 33.36 0.2 L 33.36 13.92 L 30.86 13.92 L 25.82 6.38 L 25.32 5.46 L 25.22 5.48 L 25.22 13.92 Z M 19.96 13.92 L 13.84 13.92 L 13.84 11.74 L 15.44 11.74 L 15.44 2.38 L 13.84 2.38 L 13.84 0.2 L 19.96 0.2 L 19.96 2.38 L 18.36 2.38 L 18.36 11.74 L 19.96 11.74 L 19.96 13.92 Z M 42.3 14.12 L 41.62 14.12 A 6.179 6.179 0 0 1 39.613 13.797 A 5.856 5.856 0 0 1 39.4 13.72 Q 38.36 13.32 37.57 12.53 A 5.14 5.14 0 0 1 36.615 11.193 A 6.322 6.322 0 0 1 36.34 10.56 Q 35.928 9.454 35.902 7.978 A 11.353 11.353 0 0 1 35.9 7.78 L 35.9 6.36 Q 35.9 4.76 36.35 3.58 A 6.029 6.029 0 0 1 36.964 2.376 A 5.024 5.024 0 0 1 37.59 1.61 Q 38.38 0.82 39.41 0.42 Q 40.44 0.02 41.6 0.02 L 42.28 0.02 A 6.331 6.331 0 0 1 43.909 0.224 A 5.682 5.682 0 0 1 44.51 0.42 Q 45.54 0.82 46.33 1.61 A 5.14 5.14 0 0 1 47.285 2.947 A 6.322 6.322 0 0 1 47.56 3.58 Q 47.973 4.686 47.998 6.162 A 11.353 11.353 0 0 1 48 6.36 L 48 7.78 Q 48 9.38 47.55 10.56 A 6.029 6.029 0 0 1 46.936 11.764 A 5.024 5.024 0 0 1 46.31 12.53 Q 45.52 13.32 44.49 13.72 Q 43.46 14.12 42.3 14.12 Z M 44.92 8.42 L 44.92 5.74 A 6.701 6.701 0 0 0 44.885 5.034 Q 44.847 4.68 44.769 4.376 A 3.495 3.495 0 0 0 44.69 4.11 Q 44.46 3.44 44.06 3.03 Q 43.66 2.62 43.12 2.43 A 3.402 3.402 0 0 0 42.152 2.245 A 3.929 3.929 0 0 0 41.96 2.24 A 3.609 3.609 0 0 0 40.964 2.375 A 3.355 3.355 0 0 0 40.79 2.43 Q 40.24 2.62 39.84 3.03 A 2.5 2.5 0 0 0 39.444 3.57 A 3.418 3.418 0 0 0 39.21 4.1 A 3.772 3.772 0 0 0 39.055 4.723 Q 38.98 5.173 38.98 5.72 L 38.98 8.4 A 6.701 6.701 0 0 0 39.015 9.106 Q 39.053 9.461 39.131 9.764 A 3.495 3.495 0 0 0 39.21 10.03 Q 39.44 10.7 39.84 11.11 Q 40.24 11.52 40.78 11.71 A 3.402 3.402 0 0 0 41.748 11.895 A 3.929 3.929 0 0 0 41.94 11.9 A 3.772 3.772 0 0 0 42.701 11.826 A 3.207 3.207 0 0 0 43.12 11.71 Q 43.66 11.52 44.06 11.11 A 2.5 2.5 0 0 0 44.456 10.57 A 3.418 3.418 0 0 0 44.69 10.04 A 3.772 3.772 0 0 0 44.845 9.417 Q 44.92 8.968 44.92 8.42 Z"
                />
            </g>
        </g>
    </svg>
</template>

<style lang="scss">
.precon-logo {
    .precon-logo-primary {
        fill: var(--logo-primary);
    }

    .precon-logo-secondary {
        fill: var(--logo-secondary);
    }

    .highlight {
        fill: var(--primary-500);
    }
}

.admin {
    visibility: hidden;
}

.gino {
    visibility: hidden;
}
</style>
